/**
 * What each plan entitles an account to.
 *
 * **One source, both sides.** These numbers are enforced by the API and
 * *printed* by the admin — on the registration pricing cards, in the merchant
 * settings dialog, in every "blank means the plan allowance" hint. Kept in the
 * API alone, the UI had to restate them as literals, and a literal beside a
 * rule it cannot see is a promise waiting to go stale. The pricing page saying
 * "1GB Storage" while the code enforces something else is not a display bug;
 * it is a thing a merchant paid for and did not get.
 *
 * The rules here are pure functions of plan and verification, deliberately:
 * they can be evaluated in a template as safely as in a guard.
 */

const MB = 1024 * 1024;
const GB = 1024 * MB;

export type PlanTier = 'free' | 'basic' | 'premium';

export const PLAN_TIERS: readonly PlanTier[] = ['free', 'basic', 'premium'];

/** Storage a verified account of each tier is entitled to. */
export const PLAN_QUOTA_BYTES: Record<PlanTier, number> = {
  free: 100 * MB,
  basic: 1 * GB,
  premium: 10 * GB,
};

/**
 * What an unverified *paid* account gets until someone confirms it.
 *
 * There is no payment step yet, so "basic" and "premium" are self-selected
 * labels: anyone can pick either at registration and claim 1 GB or 10 GB at our
 * expense. Until a payment can be checked, a person checks — and in the
 * meantime the account runs on an allowance generous enough to build a real
 * store and small enough that abuse is not worth the effort.
 *
 * Above the free tier deliberately: someone who chose a paid plan should not be
 * worse off than the free plan while they wait for us.
 */
export const UNVERIFIED_PAID_CAP_BYTES = 200 * MB;

/**
 * How far past the allowance an account may go before uploads stop.
 *
 * Flat, not proportional. Finishing the product you are in the middle of costs
 * the same handful of megabytes whether the plan is 100 MB or 10 GB, so scaling
 * the grace with the plan would hand the largest accounts a gigabyte of free
 * overrun to serve a need that never exceeds a few photos.
 */
export const STORAGE_GRACE_BYTES = 10 * MB;

/**
 * Normalises whatever is in the `plan` column.
 *
 * Falls back to 'basic' rather than 'free' for an unrecognised value: a plan
 * string nobody expected should not retroactively shrink a live account's
 * allowance to the smallest one on offer.
 */
export function resolvePlanTier(rawPlan: string): PlanTier {
  const v = rawPlan.trim().toLowerCase();
  if (v === 'free') return 'free';
  if (v === 'premium') return 'premium';
  return 'basic';
}

/** The headline allowance for a plan, ignoring verification. */
export function planQuotaBytes(rawPlan: string): number {
  return PLAN_QUOTA_BYTES[resolvePlanTier(rawPlan)];
}

/**
 * The storage allowance actually in force.
 *
 * Every plan requires verification, but the consequence of being unverified
 * differs, because the tiers invite different problems:
 *
 *   - **Paid** — held at the provisional allowance until confirmed. They can
 *     build meanwhile; they just cannot claim 10 GB by typing "premium" into a
 *     form.
 *   - **Free** — allowance unchanged. Applying the 200 MB provisional figure
 *     would *raise* it above the free tier's own 100 MB, which is plainly not a
 *     restriction. Free is gated at store creation instead — see
 *     {@link canCreateStores}.
 *
 * A platform admin's explicit `storageCapBytes` override outranks this and is
 * applied by the caller: a human who has set a number has already decided.
 */
export function effectivePlanQuotaBytes(rawPlan: string, planVerified: boolean): number {
  const tier = resolvePlanTier(rawPlan);
  if (tier === 'free') return PLAN_QUOTA_BYTES.free;
  return planVerified ? PLAN_QUOTA_BYTES[tier] : UNVERIFIED_PAID_CAP_BYTES;
}

/** The byte count at which uploads stop, for a given allowance. */
export function hardBlockBytes(capBytes: number): number {
  return capBytes + STORAGE_GRACE_BYTES;
}

/**
 * Whether this account may open a storefront yet.
 *
 * A free account has to be verified first. Registration is self-service and
 * costs nothing, so without a check here one person can mint any number of live
 * storefronts — each with a subdomain, a public site and a media allowance —
 * before anybody looks. Verification is that look.
 *
 * Paid plans are not gated: selecting one is at least a statement of intent,
 * and they are already held to the provisional allowance until confirmed.
 * Making them wait for a human before they can build anything would penalise
 * the accounts most likely to be genuine.
 */
export function canCreateStores(rawPlan: string, planVerified: boolean): boolean {
  return resolvePlanTier(rawPlan) !== 'free' || planVerified;
}

/**
 * Whether an account may connect its own storage bucket.
 *
 * A premium feature, and the one storage choice the platform cannot support on
 * the merchant's behalf: their bucket, their credentials, their retention — and
 * their problem if an object vanishes from it. Restricting it to the tier that
 * comes with support is as much about who we can help as what we charge.
 *
 * `byosEnabled` remains an explicit platform-admin override for an account
 * negotiated outside the tiers. It grants; it is not required of a premium
 * account, which has the feature by virtue of the plan.
 */
export function canUseByos(rawPlan: string, byosEnabled = false): boolean {
  return resolvePlanTier(rawPlan) === 'premium' || byosEnabled;
}

/**
 * The allowance as a label — "100 MB", "1 GB".
 *
 * Exists so the pricing cards and the settings hints render the same figure the
 * guards enforce, instead of a string somebody typed next to them.
 */
export function formatPlanQuota(bytes: number): string {
  return bytes >= GB
    ? `${Number((bytes / GB).toFixed(2))} GB`
    : `${Math.round(bytes / MB)} MB`;
}
